import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Button } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useApolloClient } from '@apollo/client';
import generatePath from '~client/settings/routing';
import { PropertySuccess as PropertySuccessIcon } from '~components/svgs';
import { LOGIN_TYPE } from '~constants/auth';
import { MUTATION_AGENTFORGETPASSWORD } from './queries';
import { checkAgentUserExist } from '../auth/useCheckLandlordExist';

const useStyles = makeStyles(theme => ({
  formik: {
    width: '480px',
    '& > *:not(:first-child)': {
      width: '100%',
      marginTop: 16,
    },
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  successIcon: {
    width: 112,
    height: 112,
    marginBottom: 16,
  },
  title: {
    ...theme.typography.h1,
    textAlign: 'center',
    color: theme.colors.gray9,
    marginTop: 0,
    marginBottom: 12,
  },
  heading: {
    ...theme.typography.h5,
    width: 400,
    textAlign: 'center',
    fontWeight: 'normal',
    color: theme.colors.gray7,
    padding: '0 5px',
    marginBottom: 40,
  },
  submitBtn: {
    ...theme.typography.h5,
    lineHeight: 1.5,
    padding: '9px 42px',
    marginBottom: 21,
  },
  backBtn: {
    ...theme.typography.h5,
    textAlign: 'center',
    fontWeight: 'normal',

    '& a:hover': {
      color: theme.colors.mainGreen6,
    },
  },
}));

const SendEmailSuccessful = ({ match, history }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const client = useApolloClient();
  const intervalRef = useRef(null);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [countDown, setCountDown] = useState(60);
  const onCompleted = e => {
    if (!e?.checkUserExist?.exist) {
      history.push(
        generatePath('resetAgentPassword', {
          email: decodeURIComponent(email.trim()),
        }),
      );
    }
  };

  const [queryCheckAgentUser] = checkAgentUserExist({
    onCompleted,
  });

  const email = match.params.email;
  const performCountDown = useCallback(() => {
    let initCountDown = 60;
    intervalRef.current = setInterval(() => {
      setCountDown(prevVal => (prevVal -= 1));
      initCountDown--;
      if (initCountDown === 0) {
        clearInterval(intervalRef.current);
        setBtnDisabled(false);
        setCountDown(60);
      }
    }, 1000);
  }, []);

  const handleSendResetEmailAgain = ev => {
    ev.preventDefault();
    setBtnDisabled(true);
    performCountDown();

    client
      .mutate({
        mutation: MUTATION_AGENTFORGETPASSWORD,
        variables: {
          email: decodeURIComponent(email),
          portal: 'AGENT',
        },
      })
      .then(response => {});
  };
  useEffect(() => {
    queryCheckAgentUser({
      variables: {
        value: decodeURIComponent(email?.trim()),
        type: LOGIN_TYPE.EMAIL,
      },
    });
  }, [email, queryCheckAgentUser]);

  return (
    <div className={classes.formik}>
      <div className={classes.wrapper}>
        <PropertySuccessIcon className={classes.successIcon} />

        <h1 className={classes.title}>
          {`${t('landlord.reset_password.success.title')}`}
          <br />
          {decodeURIComponent(email)}
        </h1>

        <h2 className={classes.heading}>{t('landlord.reset_password.success.heading')}</h2>

        <Form
          onSubmit={handleSendResetEmailAgain}
          className="auth__main__form auth__main__form--reset-password-success"
        >
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className={classes.submitBtn}
            disabled={btnDisabled}
          >
            <Choose>
              <When condition={btnDisabled}>
                {t('landlord.reset_password.try_again', {
                  seconds: countDown,
                })}
              </When>
              <Otherwise>{t('landlord.reset_password.success.button.re_send')}</Otherwise>
            </Choose>
          </Button>

          <div className={classes.backBtn}>
            <Link to={generatePath('agentLogin')}>{t('landlord.auth.main.button.back')}</Link>
          </div>
        </Form>
      </div>
    </div>
  );
};

SendEmailSuccessful.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      email: PropTypes.string.isRequired,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};

SendEmailSuccessful.defaultProps = {
  match: {
    params: {
      email: '',
    },
  },
  history: {
    push: () => {},
  },
};

export default SendEmailSuccessful;
